import { ErrorBanner } from '@core/components';
import type { DomainError } from '@core/domain';
import { withAppInsightsTracking } from '@core/logging/withAppInsightsTracking';
import { srOnly } from '@core/styles/utilities.css';
import { useCombinedLabels } from '@moderation/hooks';
import { getContentTypeIconComponent } from '@moderation/utils/labelIcons';
import { generateLabelSeverityOptions } from '@moderation/utils/labelSeverityOptions';
import { Button, ButtonType, Card, Heading, Icon, IconSize, LoadingSpinner, Tag, TagSize, Text, TextArea } from '@soteria/design-system-ve';
import { useCallback, useEffect, useMemo, useState } from 'react';

import * as styles from './ModerationLabelsPanel.css';

export interface ModerationLabelsPanelProps {
  /**
   * Case ID for the moderation case
   */
  caseId: string;
  /**
   * Community ID to fetch labels for
   */
  communityId: string;
  /**
   * Whether the form is currently submitting
   */
  isSubmitting: boolean;
  /**
   * Errors to display in the error banner
   */
  errors?: DomainError[] | null;
  /**
   * Currently selected label IDs (controlled)
   */
  selectedLabels: string[];
  /**
   * Current comment text (controlled)
   */
  comment: string;
  /**
   * Callback when label selection changes
   */
  onSelectionChange: (labels: string[]) => void;
  /**
   * Callback when comment text changes
   */
  onCommentChange: (comment: string) => void;
  /**
   * Callback when Apply button is clicked — applies labels to the focused card.
   * Receives the selected label IDs and optional comment.
   */
  onApply?: (labelIds: string[], comment?: string) => void;
}

/**
 * ModerationLabelsPanel Component
 *
 * Accessible moderation labels selection panel using an always-visible expanded
 * selectable list. Each label option renders as a `role="option"` item inside a
 * `role="listbox"` container with full keyboard navigation.
 *
 * Operates as a controlled component — state is owned by the parent page.
 *
 * @keyboard-navigation
 * - **Tab**: Navigate between search input, list (single tab stop via roving tabindex), comment field, and apply button
 * - **ArrowDown/ArrowUp**: Move focus between options in the list (roving tabindex)
 * - **Enter/Space**: Toggle selection of the focused option
 * - **Home/End**: Jump to first/last option in the list
 *
 * @aria-features
 * - `role="listbox"` with `aria-multiselectable="true"` on list container
 * - `role="option"` with `aria-selected` on each list item
 * - Search input with `aria-label="Search labels"`
 * - Error messages announced with aria-live="assertive"
 * - Loading states indicated with aria-busy
 * - Disabled states indicated with aria-disabled
 *
 * @wcag-compliance
 * - WCAG 2.1 AA compliant
 * - 4.5:1 contrast ratio for all text
 * - 3:1 contrast ratio for UI components
 * - Keyboard accessible without mouse
 * - Screen reader friendly with proper announcements
 */
function ModerationLabelsPanelComponent({
  caseId: _caseId, // Reserved for future use
  communityId,
  isSubmitting,
  errors,
  selectedLabels,
  comment,
  onSelectionChange,
  onCommentChange,
  onApply,
}: ModerationLabelsPanelProps) {
  // Internal search state for filtering the label list
  const [searchTerm, setSearchTerm] = useState('');

  // Roving tabindex: only the active item participates in Tab order
  const [activeIndex, setActiveIndex] = useState(0);

  // Fetch combined labels
  const { labels, loading, errors: fetchErrors } = useCombinedLabels(communityId);

  // Transform labels to option format — cross-product of label × severity
  const labelOptions = useMemo(() => generateLabelSeverityOptions(labels), [labels]);

  // Filter options by search term (case-insensitive match on display label)
  const filteredOptions = useMemo(() => {
    if (!searchTerm.trim()) {
      return labelOptions;
    }

    const lowerSearch = searchTerm.toLowerCase();

    return labelOptions.filter(option => option.label.toLowerCase().includes(lowerSearch));
  }, [labelOptions, searchTerm]);

  // Reset active index when filtered list changes (e.g. search narrows results)
  useEffect(() => {
    setActiveIndex(0);
  }, [filteredOptions.length]);

  // Toggle an item's selection state
  const handleItemToggle = useCallback(
    (optionValue: string) => {
      if (isSubmitting) return;
      const isSelected = selectedLabels.includes(optionValue);
      const newSelection = isSelected ? selectedLabels.filter(v => v !== optionValue) : [...selectedLabels, optionValue];

      onSelectionChange(newSelection);
    },
    [selectedLabels, onSelectionChange, isSubmitting]
  );

  // Keyboard navigation handler for listbox items (roving tabindex pattern)
  const handleListKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>, optionValue: string, index: number) => {
      const target = e.currentTarget;
      const keyActions: Record<string, () => void> = {
        ArrowDown: () => {
          const nextIndex = Math.min(index + 1, filteredOptions.length - 1);

          setActiveIndex(nextIndex);
          (target.nextElementSibling as HTMLElement | null)?.focus();
        },
        ArrowUp: () => {
          const prevIndex = Math.max(index - 1, 0);

          setActiveIndex(prevIndex);
          (target.previousElementSibling as HTMLElement | null)?.focus();
        },
        Home: () => {
          setActiveIndex(0);
          (target.parentElement?.firstElementChild as HTMLElement | null)?.focus();
        },
        End: () => {
          setActiveIndex(filteredOptions.length - 1);
          (target.parentElement?.lastElementChild as HTMLElement | null)?.focus();
        },
        Enter: () => handleItemToggle(optionValue),
        ' ': () => handleItemToggle(optionValue),
      };

      const action = keyActions[e.key];

      if (action) {
        e.preventDefault();
        action();
      }
    },
    [handleItemToggle, filteredOptions.length]
  );

  // Combine fetch errors with submission errors
  const allErrors = useMemo(() => {
    const errorList = [...(fetchErrors || []), ...(errors || [])];

    return errorList.length > 0 ? errorList : null;
  }, [fetchErrors, errors]);

  // Loading state
  if (loading) {
    return (
      <Card className={styles.cardWrapper} elevation="low" padding="large">
        <div aria-busy="true" className={styles.panelContent}>
          <div className={styles.loadingContainer}>
            <LoadingSpinner />
            <span className={srOnly}>Loading labels...</span>
          </div>
        </div>
      </Card>
    );
  }

  // Empty state (loading already returned above)
  if (labels.length === 0) {
    return (
      <Card className={styles.cardWrapper} elevation="low" padding="large">
        <div className={styles.panelContent}>
          <div className={styles.header}>
            <Heading level="h2">Labels</Heading>
          </div>
          <div className={styles.emptyState}>
            <Text>No labels available for this community.</Text>
          </div>
        </div>
      </Card>
    );
  }

  const isApplyDisabled = selectedLabels.length === 0 || isSubmitting;

  return (
    <Card className={styles.cardWrapper} elevation="low" padding="large">
      <div className={styles.panelContent}>
        {/* Header */}
        <div className={styles.header}>
          <Heading id="labels-heading" level="h2">
            Labels
          </Heading>
          <Text aria-describedby="labels-heading" className={styles.caseCount}>
            1 case selected
          </Text>
        </div>

        {/* Error Banner */}
        {allErrors && <ErrorBanner errors={allErrors} title="An error occurred:" />}

        {/* Unified Search + Chips Container */}
        <div
          aria-label={selectedLabels.length > 0 ? 'Selected labels and search' : undefined}
          className={styles.searchContainer}
          role={selectedLabels.length > 0 ? 'group' : undefined}
        >
          {selectedLabels.length > 0 &&
            selectedLabels.map(value => {
              const option = labelOptions.find(opt => opt.value === value);
              const displayName = option?.label ?? value;

              return (
                <Tag
                  key={value}
                  disabled={isSubmitting}
                  label={displayName}
                  removeLabel={`Remove ${displayName}`}
                  size={TagSize.Small}
                  onRemove={() => onSelectionChange(selectedLabels.filter(v => v !== value))}
                />
              );
            })}
          <input
            aria-label="Search labels"
            className={styles.searchInput}
            disabled={isSubmitting}
            placeholder="Search labels..."
            type="text"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </div>

        {/* Expanded Selectable Label List */}
        <div className={styles.listContainer}>
          <div aria-label="Label options" aria-multiselectable="true" role="listbox">
            {filteredOptions.map((option, index) => {
              const isSelected = selectedLabels.includes(option.value);
              const IconComponent = getContentTypeIconComponent(option.contentType);

              return (
                <div
                  key={option.id}
                  aria-selected={isSelected}
                  className={isSelected ? styles.listItemSelected : styles.listItem}
                  role="option"
                  tabIndex={isSubmitting ? -1 : index === activeIndex ? 0 : -1}
                  onClick={() => {
                    setActiveIndex(index);
                    handleItemToggle(option.value);
                  }}
                  onKeyDown={e => handleListKeyDown(e, option.value, index)}
                >
                  {IconComponent && (
                    <span className={styles.itemIcon}>
                      <Icon icon={IconComponent} iconSize={IconSize.XSmall} />
                    </span>
                  )}
                  <span className={styles.itemLabel}>{option.label}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Comment Section */}
        <div className={styles.commentSection}>
          <label htmlFor="comment">
            <Text>Comment (Optional)</Text>
          </label>
          <TextArea
            hideResize
            aria-label="Optional comment for moderation action"
            disabled={isSubmitting}
            id="comment"
            placeholder="Add a comment about this moderation action..."
            rows={3}
            value={comment}
            onChange={e => onCommentChange(e.target.value)}
          />
        </div>

        {/* Apply Button — dispatches APPLY_LABELS to session reducer via parent */}
        {onApply && (
          <div className={styles.applySection}>
            <Button
              aria-disabled={isApplyDisabled}
              aria-label={`Apply ${selectedLabels.length} selected ${selectedLabels.length === 1 ? 'label' : 'labels'}`}
              buttonType={ButtonType.Primary}
              disabled={isApplyDisabled}
              onClick={() => onApply(selectedLabels, comment || undefined)}
            >
              Apply
            </Button>
          </div>
        )}
      </div>
    </Card>
  );
}

export const ModerationLabelsPanel = withAppInsightsTracking(ModerationLabelsPanelComponent, 'ModerationLabelsPanel');
